import { RTKTags, api } from '@/store/api'

import Env from '../../../env/env'

import type {
  deleteEnterpriseUserRequest,
  updateEnterpriseUserRoleRequest,
  inviteEnterpriseNewUserRequest,
  UpdateEnterpriseData,
  deleteInvitationUserRequest,
  GetExpiredReportsRequest,
  IExpiredReportsResponse,
  IExpiredReportsCache,
} from './Enterprise.types'

export const enterpriseApi = api.injectEndpoints({
  endpoints: (builder) => ({
    updateEnterpriseUserRole: builder.mutation<
      void,
      updateEnterpriseUserRoleRequest
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/enterprises/users/${request.id}/change-role`,
        method: 'PATCH',
        body: {
          role: request.role,
        },
      }),
      invalidatesTags: [
        RTKTags.enterpriseUsers,
        RTKTags.workspaceDetails,
        RTKTags.getWorkspaces,
      ],
    }),

    deleteEnterpriseUser: builder.mutation<void, deleteEnterpriseUserRequest>({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/enterprises/users/${request.userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        RTKTags.enterpriseUsers,
        RTKTags.workspaceDetails,
        RTKTags.getWorkspaces,
      ],
    }),

    inviteEnterpriseNewUser: builder.mutation<
      void,
      inviteEnterpriseNewUserRequest
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/enterprises/send-invite`,
        method: 'POST',
        body: {
          email: request.email,
          role: request.role,
        },
      }),
      invalidatesTags: [RTKTags.enterpriseUsers],
    }),

    updateEnterpriseData: builder.mutation<void, UpdateEnterpriseData>({
      query: (request: UpdateEnterpriseData) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/enterprises/${request.enterpriseId}`,
        method: 'PUT',
        body: {
          companyName: request.companyName,
          companyAddress: request.companyAddress,
        },
      }),
      invalidatesTags: [RTKTags.enterpriseInfo],
    }),

    deleteInvitationUser: builder.mutation<void, deleteInvitationUserRequest>({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v2/enterprises/remove-invitation/workspaces/${request.workspaceId}/teams/${request.teamId}/user/${request.userId}/invite/${request.inviteMemberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [RTKTags.enterpriseUsers, RTKTags.workspaceDetails],
    }),

    getExpiredEnterpriseReports: builder.query<
      IExpiredReportsCache,
      GetExpiredReportsRequest
    >({
      query: (params) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v2/enterprises/reports/expired`,
        method: 'GET',
        params: {
          ...(params.workspaceId && { workspaceId: params.workspaceId }),
          ...(params.teamId && { teamId: params.teamId }),
          page: params.page,
          pageSize: params.pageSize,
          ...(params.searchByString && {
            searchByString: params.searchByString,
          }),
        },
      }),
      transformResponse: (response: IExpiredReportsResponse) => ({
        list: (response.info?.list ?? []).map((item: any) => ({
          ...item,
          id: item.id || item._id,
        })) as IExpiredReportsCache['list'],
        totalItems: response.info?.totalItems ?? 0,
        reportRetentionDays: response.info?.reportRetentionDays ?? null,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const {
          workspaceId = '_',
          teamId = '_',
          pageSize,
          searchByString,
        } = queryArgs

        return `${endpointName}:${workspaceId}:${teamId}:${pageSize}:${searchByString}`
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page,
      merge: (currentCache, newData, { arg }) => {
        if (
          currentCache &&
          arg.searchByString !== undefined &&
          arg.searchByString !== ''
        ) {
          return newData
        }

        if (!newData.list.length) {
          return { ...currentCache, totalItems: newData.totalItems }
        }

        return {
          list: [...currentCache.list, ...newData.list],
          totalItems: newData.totalItems,
          reportRetentionDays: newData.reportRetentionDays,
        }
      },
      keepUnusedDataFor: 0,
      providesTags: [RTKTags.monitoringCenter],
    }),

    updateEnterpriseConfig: builder.mutation<
      void,
      { reportRetentionDays: number }
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/enterprises/config`,
        method: 'PATCH',
        body: request,
      }),
      invalidatesTags: [RTKTags.userInfo],
    }),
  }),
})

export const {
  useUpdateEnterpriseUserRoleMutation,
  useDeleteEnterpriseUserMutation,
  useInviteEnterpriseNewUserMutation,
  useUpdateEnterpriseDataMutation,
  useDeleteInvitationUserMutation,
  useGetExpiredEnterpriseReportsQuery,
  useUpdateEnterpriseConfigMutation,
} = enterpriseApi
